import {createApi} from '@reduxjs/toolkit/query/react';
import {AxiosRequestHeaders} from 'axios';

import httpBaseQuery from '../../../shared/api/rtk-query/httpBaseQuery';
import {ApiUrls} from '../../../shared/config/model/urls';
import IDomainMonitorOutput, {IDomainMonitorItem} from '../model/DomainMonitor/IDomainMonitor';
import CreateDomainMonitorType from '../model/DomainMonitor/CreateDomainMonitorType';
import UpdateDomainMonitorType from '../model/DomainMonitor/UpdateDomainMonitorType';
import ISummaryDomainMonitors from '../model/DomainMonitor/ISummaryDomainMonitors';
import IOrderBy from '../../../shared/model/IOrderBy';
import IHydraResponse from '../../../shared/api/model/HydraResponse';
import IPagination from '../../../shared/api/model/IPagination';
import ICollectionParams from '../../../shared/api/model/ICollectionParams';
import {paginationAdapter} from '../../../shared/api/paginationHelper';
import {providesListUuid} from '../../../shared/api/rtk-query/RTKQueryHelper';
import {buildQuery} from '../../../shared/api/query';

export const domainMonitorAPI = createApi({
    reducerPath: 'domainMonitorAPI',
    baseQuery: httpBaseQuery(),
    tagTypes: ['DomainMonitor', 'SummaryDomainMonitors'],
    refetchOnMountOrArgChange: 60,
    refetchOnFocus: true,
    refetchOnReconnect: true,
    endpoints: (build) => ({
        addDomainMonitor: build.mutation<IDomainMonitorOutput, CreateDomainMonitorType>({
            query: (data) => ({
                path: ApiUrls.DOMAIN_MONITORS(),
                method: 'POST',
                data,
            }),
            invalidatesTags: [{ type: 'DomainMonitor', id: 'LIST' }, 'SummaryDomainMonitors'],
        }),
        getDomainMonitors: build.query<IPagination<IDomainMonitorOutput>, ICollectionParams>({
            query: ({page, sort, filters}) => ({
                path: buildQuery(ApiUrls.DOMAIN_MONITORS(page, sort, filters)),
                headers: {Accept: 'application/ld+json'} as unknown as AxiosRequestHeaders,
            }),
            providesTags: (result) => providesListUuid(result?.items, 'DomainMonitor'),
            transformResponse(
                rawResult: IHydraResponse<IDomainMonitorOutput>,
            ): Promise<IPagination<IDomainMonitorOutput>> | IPagination<IDomainMonitorOutput> {
                return paginationAdapter(rawResult);
            },
        }),
        getDomainMonitor: build.query<IDomainMonitorItem, string>({
            query: (uuid) => ({
                path: ApiUrls.DOMAIN_MONITOR(uuid),
            }),
            providesTags: (result, error, uuid) => [{ type: 'DomainMonitor', id: uuid }],
        }),
        updateDomainMonitor: build.mutation<IDomainMonitorOutput, UpdateDomainMonitorType>({
            query: ({uuid, ...data}) => ({
                path: ApiUrls.DOMAIN_MONITOR(uuid ?? ''),
                method: 'PUT',
                data,
            }),
            async onQueryStarted({ uuid, ...patch }, { dispatch }) {
                dispatch(
                    domainMonitorAPI.util.updateQueryData('getDomainMonitor', uuid ?? '', (draft) => {
                        Object.assign(draft, patch);
                    }),
                );
            },
            invalidatesTags: [{ type: 'DomainMonitor' }, 'SummaryDomainMonitors'],
        }),
        deleteDomainMonitor: build.mutation<void, string>({
            query: (uuid) => ({
                path: ApiUrls.DOMAIN_MONITOR(uuid),
                method: 'DELETE',
            }),
            invalidatesTags: () => [{ type: 'DomainMonitor', id: 'LIST' }, 'SummaryDomainMonitors'],
        }),
        getSummaryDomainMonitors: build.query<ISummaryDomainMonitors, void>({
            query: () => ({
                path: ApiUrls.SUMMARY_DOMAIN_MONITORS,
            }),
            providesTags: ['SummaryDomainMonitors'],
        }),
        getAllDomainMonitorIds: build.query<string[], {sort?: IOrderBy, filters?: string[]}>({
            query: ({sort, filters}) => ({
                path: ApiUrls.DOMAIN_MONITORS_IDS(sort, filters),
                headers: {Accept: 'application/json'} as unknown as AxiosRequestHeaders,
            }),
        }),
    }),
});
